// © Microsoft Corporation. All rights reserved.

import * as ReactDOM from "react-dom";

import {
    ButtonItemType,
    ButtonType,
    SharingToolbarHandledProps,
    SharingToolbarProps,
    SharingToolbarUnHandledProps
} from "./SharingToolbar.props";
import { Glyph, Icon } from "@msnews/icon";
import { SharingToolbarStyles, contextMenuStyles } from "./SharingToolbar.styles";
import { get, merge } from "lodash-es";

import { CardContextMenu } from "@msnews/card-context-menu";
import { ComponentStyles } from "@microsoft/fast-jss-manager";
import { DesignSystem } from "@microsoft/fast-components-styles-msft";
import { DialogArticleCard } from "../dialog-article-card";
import { DialogClassNameContract } from "@microsoft/fast-components-react-base";
import Foundation from "@microsoft/fast-components-foundation-react";
import { HandledPropsKeys } from "@msnews/core";
import React from "react";
import { ShareDialog } from "@msnews/share-dialog";
import { SharingToolbarClassNames } from ".";
import { SocialMediaIcon } from "@msnews/social-media-icon";
import { Status } from "../../ContentActionsToolbar.constants";
import { ToolbarPosition } from "../../ContentActionsToolbar.props";
import { moreOptionsMenuDefaultOffsetPos } from "./SharingToolbar.constants";

/**
 * The local state interface of the component
 */
export interface SharingToolbarState {
    /** Flag to determine if share dialog should be displayed */
    shareDialogOpened: boolean
}

/**
 * Social media sharing toolbar component
 * @class - SharingToolbar
 * @classdesc - Sharing toolbar component class
 */
export class SharingToolbar extends Foundation<SharingToolbarHandledProps, SharingToolbarUnHandledProps, SharingToolbarState> {
    /**
     * Describes the object that enumerates all handled props for a component.
     * @protected
     * @type { HandledPropsKeys<SharingToolbarHandledProps> }
     */
    protected handledProps: HandledPropsKeys<SharingToolbarHandledProps> = {
        managedClasses: undefined,
        position: undefined,
        sharingButtons: undefined
    };

    /**
     * Stylesheet to apply to article card
     * @type {Partial<ComponentStyles<DialogClassNameContract, DesignSystem>>}
     */
    private articleCardStylesheet: Partial<ComponentStyles<DialogClassNameContract, DesignSystem>>;

    /**
     * Constructs a SharingToolbar component
     * @constructor
     * @param { SharingToolbarProps } props props contract
     */
    constructor(props: SharingToolbarProps) {
        super(props);
        this.state = {
            shareDialogOpened: false
        };
    }

    /**
     * Render sharing toolbar component
     * @public
     * @return {JSX.Element}
     */
    public render(): JSX.Element {
        return (
            <div {...this.unhandledProps()} >
                <ul className={this.getPositioningClassName()} >
                    {this.renderSharingButtons()}
                </ul>
                {this.renderShareDialog()}
            </div>
        );
    }

    /**
     * Get the classname based on positioning of the toolbar
     */
    private getPositioningClassName(): string {
        const positionClass: string = this.props.position === ToolbarPosition.horizontal ?
            this.managedClass("iconListHorizontal") :
            this.managedClass("iconListVertical");

        return positionClass;
    }

    /**
     * Render the toolbar sharing buttons
     * @private
     * @return {JSX.Element} The sharing buttons element
     */
    private renderSharingButtons(): JSX.Element[] {
        if (!this.props.sharingButtons) {
            return ([]);
        }

        return this.props.sharingButtons.map(buttonItem => {
            if (!buttonItem.buttonData) {
                return null;
            }

            const buttonId: string = buttonItem.buttonType === ButtonType.SocialMedia ? buttonItem.buttonData.mediaType.toString() : buttonItem.buttonType.toString();
            let listItem: JSX.Element;

            switch (buttonItem.buttonType) {
                case ButtonType.SocialMedia:
                    listItem = this.renderSocialMediaIcon(buttonItem);
                    break;
                case ButtonType.ShareDialog:
                    listItem = this.renderShareDialogIcon(buttonItem);
                    break;
                case ButtonType.LikeDislike:
                    listItem = this.renderLikeDislikeIcon(buttonItem);
                    break;
                case ButtonType.MoreOptions:
                    listItem = this.renderMoreOptionsIcon(buttonItem);
                    break;
                default:
                    return (null);
            }

            return (
                <li key={buttonId} className={this.managedClass("iconListItem")}>
                    {listItem}
                </li>
            );
        });
    }

    /**
     * Renders the share dialog
     * @private
     * @return {JSX.Element} The share dialog element
     */
    private renderShareDialog(): JSX.Element {
        const shareDialogItem = this.props.sharingButtons.find(x => x.buttonType === ButtonType.ShareDialog);
        if (!shareDialogItem || !shareDialogItem.buttonData) {
            return null;
        }

        const { shareDialogProps, dialogArticleCardProps, articleTelemetryTags } = shareDialogItem.buttonData;
        if (!this.state.shareDialogOpened || !shareDialogProps || !dialogArticleCardProps) {
            return null;
        }

        const shareDialog = (
            <ShareDialog
                {...shareDialogProps}
                onClose={this.onShareDialogClose}
            >
                <DialogArticleCard
                    {...dialogArticleCardProps}
                    {...articleTelemetryTags}
                    jssStyleSheet={this.articleCardStylesheet}
                />
            </ShareDialog>
        );

        return ReactDOM.createPortal(
            shareDialog,
            document.body
        );
    }

    /**
     * Renders the social media icon element
     * @private
     * @param {ButtonItemType} buttonItem The button item data
     * @return {JSX.Element} The share dialog element
     */
    private renderSocialMediaIcon(buttonItem: ButtonItemType): JSX.Element {
        const { attributes, mediaType, contentUrl, contentTitle, icon, ocid, target, newWindowTelemetryCall } = buttonItem.buttonData;
        if (!mediaType || !contentUrl || !contentTitle) {
            return null;
        }

        return (
            <SocialMediaIcon
                jssStyleSheet={SharingToolbarStyles}
                socialMedia={mediaType}
                contentUrl={contentUrl}
                contentTitle={contentTitle}
                hideLabel={true}
                socialMediaItemConfigOverride={{ icon: icon, ocid: ocid, target: target, newWindowTelemetryCall: newWindowTelemetryCall }}
                {...attributes}
                {...buttonItem.telemetryTags}
            />
        );
    }

    /**
     * Renders the share dialog icon element
     * @private
     * @param {ButtonItemType} buttonItem The button item data
     * @return {JSX.Element} The share dialog icon element
     */
    private renderShareDialogIcon(buttonItem: ButtonItemType): JSX.Element {
        const { attributes, shareDialogProps, dialogArticleCardProps, onSelect } = buttonItem.buttonData;
        if (!shareDialogProps || !dialogArticleCardProps || !shareDialogProps.enabled || !shareDialogProps.contentTitle || !shareDialogProps.contentUrl) {
            return null;
        }

        return (
            <button
                className={this.managedClass("iconContainer")}
                data-piitxt={"tmx-stb-sharedialog"}
                onClick={(event: any) => this.onShareDialogOpen(event, onSelect)}
                {...attributes}
                {...buttonItem.telemetryTags}
            >
                <Icon glyphType={Glyph.Share} className={this.managedClass("iconImage")} />
            </button>
);
    }

    /**
     * Renders the like/dislike icon element
     * @private
     * @param {ButtonItemType} buttonItem The button item data
     * @return {JSX.Element} The like/dislike icon element
     */
    private renderLikeDislikeIcon(buttonItem: ButtonItemType): JSX.Element {
        if (!buttonItem.buttonData.status) {
            return;
        }
        const { attributes, status, onSelect } = buttonItem.buttonData;

        const glyphType = status !== Status.DisLike ?
            (status === Status.Like ? Glyph.LikedText : Glyph.LikeText) :
            Glyph.DislikeText;

        const iconClassName = this.managedClass("icon_" + status.toString() as keyof SharingToolbarClassNames);
        return (
            <div className={iconClassName}>
                <button
                    className={this.managedClass("iconContainer")}
                    data-piitxt={"tmx-stb-likedislike"}
                    onClick={onSelect || undefined}
                    {...attributes}
                    {...buttonItem.telemetryTags}
                >
                    <Icon glyphType={glyphType} className={this.managedClass("iconImage")} />
                </button>
            </div>
        );
    }

    /**
     * Renders the more options icon element
     * @private
     * @param {ButtonItemType} buttonItem The button item data
     * @return {JSX.Element} The more options icon element
     */
    private renderMoreOptionsIcon(buttonItem: ButtonItemType): JSX.Element {
        if (!buttonItem.buttonData || !buttonItem.buttonData.status || !buttonItem.buttonData.menuItems) {
            return;
        }
        const { attributes, menuItems, offsetPos, zIndex } = buttonItem.buttonData;

        const positionOffset = this.props.position === ToolbarPosition.vertical
        ? { ...moreOptionsMenuDefaultOffsetPos, ...offsetPos }
        : undefined;

        return (
            <div
                className={this.managedClass("moreOptions")}
                {...attributes}
            >
                <CardContextMenu
                    jssStyleSheet={contextMenuStyles}
                    enabled={true}
                    menuItems={menuItems}
                    positionOffset={positionOffset}
                    zIndex={zIndex}
                    telemetryTag={buttonItem.telemetryTags}
                />
            </div>
        );
    }

    /**
     * Enables and open the share dialog
     * @private
     * @param {any} event Event
     * @param {(e: any) => void)} onSelect Telemetry callback
     */
    private onShareDialogOpen = (event: any, onSelect: (e: any) => void): void => {
        this.setState({ shareDialogOpened: true });
        if (onSelect) {
            onSelect(event);
        }
    }

    /**
     * Disables and close the share dialog
     * @private
     */
    private onShareDialogClose = (): void => {
        this.setState({ shareDialogOpened: false });
    }

    /**
     * Utility function to simplify getting class name from managedClasses.
     * @param className - The class to get from managedClasses
     * @returns The managed class name
     */
    private managedClass(className: keyof SharingToolbarClassNames): string {
        return get(this.props.managedClasses, `${className}`);
    }
}

export * from "./SharingToolbar.props";